// What the owner area knows about who is looking at it.
//
// The answer comes from GET /api/owner/accounts/me and nothing else. The browser
// cannot read the session cookie -- it is HttpOnly, deliberately -- so asking
// the server is the only honest way to know, and it answers all three states at
// once: 401 for signed out, null for signed in with no restaurant yet, and the
// restaurant for everyone else.

import { api } from '../api/client';
import type { components } from '../api/types';
import { tabForgotten, tabLoaded } from './tab';

export type Account = components['schemas']['AccountBody'];

export type SessionState =
	| { status: 'loading' }
	| { status: 'signedOut' }
	| { status: 'noRestaurant' }
	| { status: 'ready'; account: Account }
	| { status: 'failed' };

let state = $state<SessionState>({ status: 'loading' });

// Every answer goes through here, so the tab names the restaurant on screen
// (src/owner/tab.ts) and none once it is signed out or has none.
function set(next: SessionState): void {
	if (next.status === 'ready') tabLoaded(next.account.id);
	else if (next.status === 'signedOut' || next.status === 'noRestaurant') tabForgotten();
	state = next;
}

export function session(): SessionState {
	return state;
}

/** Ask the server who this is. Safe to call again; the last answer wins. */
export async function loadSession(): Promise<void> {
	try {
		const { data, error, response } = await api.GET('/api/owner/accounts/me');

		if (response.status === 401) {
			set({ status: 'signedOut' });
			return;
		}
		if (error !== undefined || !response.ok) {
			// Not treated as signed out. Telling someone to sign in again when
			// the server is briefly unreachable loses their place for no reason.
			set({ status: 'failed' });
			return;
		}
		// null is an answer, not an absence: signed in, restaurant not made yet.
		set(data ? { status: 'ready', account: data } : { status: 'noRestaurant' });
	} catch {
		// A fetch that never reaches the server throws rather than returning an
		// error field: no network, a blocked request, a wrong origin. Without
		// this the promise rejects, nothing sets a state, and the screen stays
		// on "Loading..." for ever -- the one outcome the failed state exists to
		// prevent. Found in a browser; no type checker can see it.
		set({ status: 'failed' });
	}
}

/**
 * Read the restaurant again behind a dashboard already on screen. Quiet: only
 * an answer changes anything, so a failed refresh leaves the dashboard as it
 * was rather than swapping it for the load-failed screen.
 */
export async function refreshSession(): Promise<void> {
	if (state.status !== 'ready') return;
	try {
		const { data, response } = await api.GET('/api/owner/accounts/me');
		if (response.status === 401) {
			set({ status: 'signedOut' });
			return;
		}
		if (data) set({ status: 'ready', account: data });
	} catch {
		// Kept as it was; the next refresh asks again.
	}
}

/** Record a restaurant the owner has just created, with no second round trip. */
export function restaurantCreated(account: Account): void {
	set({ status: 'ready', account });
}

/** End the session, then show the sign-in screen.
 *
 * The server revokes the row and clears the cookie; this only has to stop
 * showing a dashboard for a session that no longer exists. A failure still
 * signs out locally: whatever went wrong, continuing to show the dashboard is
 * the one thing that must not happen.
 */
export async function signOut(): Promise<void> {
	try {
		await api.POST('/auth/signout');
	} catch {
		// Deliberately ignored, for the reason above.
	}
	set({ status: 'signedOut' });
}
